import { DataRequestMessageSchema, type CollectionRegistry, type Message } from '../types';
import { logger } from '../utils/logger';
import { ThreadManager } from '../threads';
import { queryCache } from '../utils/query-cache';
import { formatToolResultForLLM } from '../userResponse/llm-result-truncator';
import { stableStringify } from '../utils/cache-key';

// Loose tool shape used solely for cache-policy lookup. Tools registered via
// sdk.setTools() may carry richer fields (id, cache, etc.) beyond the LLM
// `Tool` type — we read `id` and `cache` here without depending on a strict
// type to avoid coupling caching to the LLM tool definition.
type ToolWithCachePolicy = { id?: string; cache?: false | { ttlMs?: number }; [key: string]: any };

/**
 * Get cache key for a query - handles both string and object queries
 * Must match the logic in base-llm.ts getQueryCacheKey
 */
function getQueryCacheKey(query: any): string {
  if (typeof query === 'string') {
    return query;
  } else if (query?.sql) {
    const values = query.values || query.params;
    if (values && Object.keys(values).length > 0) {
      return JSON.stringify({ sql: query.sql, values });
    }
    return query.sql;
  }
  return '';
}

/**
 * Get cache key for any cacheable operation.
 * Only database queries are cached — non-database tools (Excel, API, forms) are NOT cached.
 *
 * @param tools — tool registry (passed in from sdk.tools); used to honor the
 *   `cache: false` flag declared on individual tool definitions (server-side
 *   `Cache-Control: no-store` semantics).
 */
function getCacheKey(collection: string, op: string, params: any, tools?: ToolWithCachePolicy[]): string {
  // database.execute — existing behavior
  if (collection === 'database' && op === 'execute' && params?.sql) {
    return getQueryCacheKey(params.sql);
  }

  // external-tools.execute — sql-based tool (sql comes through as a param)
  if (collection === 'external-tools' && op === 'execute' && params?.sql) {
    const toolId = params.toolId || '';
    const sqlKey = getQueryCacheKey(params.sql);
    // Include params/filterParams in key for parameterized queries
    const paramsKey = params.params ? JSON.stringify(params.params) : '';
    return sqlKey ? `et:${toolId}:${sqlKey}:${paramsKey}` : '';
  }

  // external-tools.execute — direct tool (sql is built internally, not passed as param)
  // Cache key must include ALL params that affect the result, not just `data`.
  // Direct tools receive filter params (branch, targetBranch, itemCodes, etc.)
  // as siblings of `data` via tool-executor's spread of externalTool.parameters.
  // Earlier versions keyed only on `data`, so every call to a tool collided in
  // the cache regardless of filters — workflows and dashboard components were
  // silently returning stale results from the first call.
  if (collection === 'external-tools' && op === 'execute' && params?.toolId && !params?.sql) {
    // Server-declared opt-out: tool definitions can set `cache: false` to
    // mark themselves non-cacheable (live data like orders lists, write
    // operations whose responses must not be memoized). Mirrors HTTP
    // `Cache-Control: no-store`; the data owner declares the policy, every
    // caller automatically gets correct behavior. Empty key = skip cache
    // lookup AND skip storing the result.
    const tool = tools?.find((t) => t.id === params.toolId);
    if (tool?.cache === false) return '';

    // Strip toolId/toolName (toolId is already in the key prefix; toolName is
    // a label, not part of the query identity) and stably stringify the rest.
    const { toolId, toolName, ...rest } = params;
    const paramsKey = stableStringify(rest);
    return `et-direct:${toolId}:${paramsKey}`;
  }

  // external-tools.executeByQueryId — always a database query
  if (collection === 'external-tools' && op === 'executeByQueryId' && params?.queryId) {
    const toolId = params.toolId || '';
    const filterKey = params.filterParams ? JSON.stringify(params.filterParams) : '';
    const paramsKey = params.params ? JSON.stringify(params.params) : '';
    return `etq:${toolId}:${params.queryId}:${paramsKey}:${filterKey}`;
  }

  return '';
}

/**
 * Handle incoming data_req messages and execute collection handlers
 *
 * @param tools — tool registry from `sdk.tools`. Optional; passed when
 *   available so per-tool cache policies (`cache: false`) are honored.
 */
export async function handleDataRequest(
  data: any,
  collections: CollectionRegistry,
  sendMessage: (message: Message) => void,
  tools?: ToolWithCachePolicy[]
): Promise<void> {
  // Extract request info before try block so catch can send error responses
  let requestId: string | undefined;
  let collection: string | undefined;
  let op: string | undefined;

  try {
    const dataRequest = DataRequestMessageSchema.parse(data);
    const { id, payload } = dataRequest;
    requestId = id;
    const { collection: col, op: operation, params, SA_RUNTIME } = payload;
    collection = col;
    op = operation;

    // Check if collection and operation exist
    if (!collections[collection]) {
      sendDataResponse(id, collection, op, null, {
        error: `Collection '${collection}' not found`,
      }, sendMessage);
      return;
    }

    if (!collections[collection][op]) {
      sendDataResponse(id, collection, op, null, {
        error: `Operation '${op}' not found for collection '${collection}'`,
      }, sendMessage);
      return;
    }

    // Check cache for database and external-tools database queries
    const startTime = performance.now();
    let result: any;
    let fromCache = false;
    const cacheKey = getCacheKey(collection, op, params, tools);

    if (cacheKey) {
      const cachedResult = queryCache.get(cacheKey);
      if (cachedResult !== null) {
        result = cachedResult;
        fromCache = true;
        logger.info(`[QueryCache] Returning cached result for ${collection}.${op}`);
      }
    }

    // Execute the handler if not cached
    if (!fromCache) {
      const handler = collections[collection][op];

      // For database execute, ensure params.sql is a string (not object)
      // The database handler expects either plain SQL or JSON string
      let handlerParams = params || {};
      if (collection === 'database' && op === 'execute' && params?.sql && typeof params.sql !== 'string') {
        // Convert object query to JSON string format
        const queryKey = getQueryCacheKey(params.sql);
        handlerParams = { ...params, sql: queryKey };
        logger.debug(`[data-request] Converted object query to JSON string for database handler`);
      }

      result = await handler(handlerParams);

      // Store result in cache for database query operations
      if (cacheKey && result) {
        queryCache.set(cacheKey, result);
      }
    }

    const executionMs = Math.round(performance.now() - startTime);

    logger.info(`Executed ${collection}.${op} in ${executionMs}ms${fromCache ? ' (from cache)' : ''}`);

    // Update UIBlock with component data if SA_RUNTIME has uiBlockId
    if (SA_RUNTIME && typeof SA_RUNTIME === 'object' && 'uiBlockId' in SA_RUNTIME) {
      const uiBlockId = (SA_RUNTIME as any).uiBlockId;
      const threadId = (SA_RUNTIME as any).threadId;

      const threadManager = ThreadManager.getInstance();
      let uiBlock = null;
      let thread = null;

      // If threadId is provided, get the specific thread
      if (threadId) {
        thread = threadManager.getThread(threadId);
        if (thread) {
          uiBlock = thread.getUIBlock(uiBlockId);
        }
      } else {
        // Otherwise search across all threads
        const result = threadManager.findUIBlockById(uiBlockId);
        if (result) {
          thread = result.thread;
          uiBlock = result.uiBlock;
        }
      }

      // Update UIBlock's componentData with a summary (not full result) to save memory
      // The full result is sent to the frontend via WebSocket, no need to store it
      if (uiBlock) {
        // Use formatter to extract proper metadata including totalItems, totalDeadstockItems, etc.
        const formattedResult = formatToolResultForLLM(result, {
          maxRows: 3,           // Only need a few sample rows for summary
          maxCharsPerField: 100 // Short truncation for summary
        });

        const dataSummary = {
          _dataReceived: true,
          _timestamp: new Date().toISOString(),
          _collection: collection,
          _operation: op,
          _totalRecords: formattedResult.summary.totalRecords,
          _recordsShown: formattedResult.summary.recordsShown,
          _metadata: formattedResult.metadata, // Preserve totalItems, totalDeadstockItems, etc.
          _schema: formattedResult.summary.schema
        };
        uiBlock.setComponentData(dataSummary);
        logger.info(`Updated UIBlock ${uiBlockId} with data summary from ${collection}.${op} (${formattedResult.summary.totalRecords} total records)`);
      } else {
        logger.warn(`UIBlock ${uiBlockId} not found in threads`);
      }
    }

    // Send response
    sendDataResponse(id, collection, op, result, { executionMs }, sendMessage);
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    logger.error('Failed to handle data request:', error);

    // Send error response back to the frontend (instead of silently dropping it)
    if (requestId) {
      sendDataResponse(requestId, collection || 'unknown', op || 'unknown', null, {
        error: errorMsg,
      }, sendMessage);
    }
  }
}

/**
 * Send a data_res response message
 */
function sendDataResponse(
  id: string,
  collection: string,
  op: string,
  data: any,
  meta: { executionMs?: number; error?: string },
  sendMessage: (message: Message) => void
): void {
  const response: Message = {
    id,
    from: { type: 'data-agent' },
    type: 'DATA_RES',
    payload: {
      collection,
      op,
      data,
      ...meta,
    },
  };

  sendMessage(response);
}
